import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export const useUnreadCount = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: count = 0 } = useQuery({
    queryKey: ['unread-notification-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count: c } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      return c || 0;
    },
    enabled: !!user,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  // Realtime subscription with 30s debounce to reduce invalidation storms
  useEffect(() => {
    if (!user) return;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const channel = supabase
      .channel('unread-count')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          if (debounceTimer) return; // skip if already scheduled
          debounceTimer = setTimeout(() => {
            debounceTimer = null;
            queryClient.invalidateQueries({ queryKey: ['unread-notification-count', user.id] });
          }, 30_000);
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  return count;
};

/** Imperatively refresh the unread count from anywhere */
export const useInvalidateUnreadCount = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useCallback(() => {
    if (user) {
      queryClient.invalidateQueries({ queryKey: ['unread-notification-count', user.id] });
    }
  }, [queryClient, user]);
};

// Helper to create notifications from frontend actions
export const createNotification = async (params: {
  userId: string; // recipient
  type: 'like' | 'comment' | 'reply' | 'points_post' | 'points_invite' | 'system';
  actorId?: string;
  postId?: string;
  commentId?: string;
  pointsAmount?: number;
  title?: string;
  body?: string;
}) => {
  // Don't notify yourself
  if (params.actorId && params.actorId === params.userId) return;

  const { error } = await supabase.from('notifications').insert({
    user_id: params.userId,
    type: params.type,
    actor_id: params.actorId || null,
    post_id: params.postId || null,
    comment_id: params.commentId || null,
    points_amount: params.pointsAmount || null,
    title: params.title || null,
    body: params.body || null,
  });

  if (error) console.error('Create notification error:', error);
};
